import { LitElement, html, css } from 'lit';
import { router } from '../lib/router.js';
import { store } from '../lib/store.js';

export class SiteFooter extends LitElement {
  static properties = { _settings: { state: true } };
  static styles = css`
    :host {
      display: block;
      background: var(--color-bg-glass);
      border-top: 1px solid var(--color-border, rgba(92,105,168,0.16));
      backdrop-filter: blur(18px);
      -webkit-backdrop-filter: blur(18px);
    }
    .footer-inner {
      max-width: var(--content-max-width, 1440px);
      margin: 0 auto;
      padding: var(--space-16, 4rem) var(--content-padding, 2rem) var(--space-8, 2rem);
    }
    .grid {
      display: grid;
      grid-template-columns: 2fr 1fr 1fr 1fr;
      gap: var(--space-12, 3rem);
      margin-bottom: var(--space-12, 3rem);
    }
    .brand {
      display: flex;
      flex-direction: column;
      gap: var(--space-4, 1rem);
    }
    .brand-logo {
      display: flex;
      align-items: center;
      gap: var(--space-3, 0.75rem);
    }
    .brand-icon {
      width: 3.25rem;
      height: 2.35rem;
      background: var(--color-accent);
      border-radius: var(--border-radius-sm, 0.375rem);
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 800;
      font-size: 1.35rem;
      font-style: italic;
      color: white;
    }
    .brand-name {
      font-weight: var(--font-medium, 500);
      font-size: var(--text-sm, 0.875rem);
      color: var(--color-text-primary, #15164a);
    }
    .brand-desc {
      font-size: var(--text-sm, 0.875rem);
      color: var(--color-text-muted, #6b6b80);
      line-height: var(--leading-relaxed, 1.7);
      max-width: 320px;
    }
    .col-title {
      font-size: var(--text-xs, 0.75rem);
      font-weight: var(--font-semibold, 600);
      text-transform: uppercase;
      letter-spacing: 0.08em;
      color: var(--color-text-muted, #6b6b80);
      margin-bottom: var(--space-4, 1rem);
    }
    .col-links {
      display: flex;
      flex-direction: column;
      gap: var(--space-3, 0.75rem);
    }
    .col-link {
      font-size: var(--text-sm, 0.875rem);
      color: var(--color-text-secondary, #a0a0b8);
      text-decoration: none;
      transition: color var(--transition-fast, 0.15s ease);
      cursor: pointer;
    }
    .col-link:hover {
      color: var(--color-accent, #00d4ff);
    }
    .bottom {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-top: var(--space-8, 2rem);
      border-top: 1px solid var(--color-border, rgba(255,255,255,0.08));
    }
    .copyright {
      font-size: var(--text-sm, 0.875rem);
      color: var(--color-text-muted, #6b6b80);
    }
    .social {
      display: flex;
      gap: var(--space-4, 1rem);
    }
    .social-link {
      color: var(--color-text-muted, #6b6b80);
      transition: color var(--transition-fast, 0.15s ease);
      cursor: pointer;
    }
    .social-link:hover {
      color: var(--color-accent, #00d4ff);
    }

    @media (max-width: 768px) {
      .grid {
        grid-template-columns: 1fr 1fr;
        gap: var(--space-8, 2rem);
      }
      .brand { grid-column: 1 / -1; }
      .bottom {
        flex-direction: column;
        gap: var(--space-4, 1rem);
        text-align: center;
      }
    }
    @media (max-width: 480px) {
      .grid {
        grid-template-columns: 1fr;
      }
    }
  `;

  constructor() { super(); this._settings = null; this._onStoreUpdate = () => { this._settings = store.getSiteSettings(); this.requestUpdate(); }; }
  connectedCallback() { super.connectedCallback(); this._unsub = store.subscribe(this._onStoreUpdate); this._settings = store.getSiteSettings(); }
  disconnectedCallback() { super.disconnectedCallback(); if (this._unsub) this._unsub(); }

  render() {
    const brand = this._settings?.brand || {};
    const footer = this._settings?.footer || { columns: [], social: [], copyright: '' };
    return html`
      <div class="footer-inner">
        <div class="grid">
          <div class="brand">
            <div class="brand-logo">
              <div class="brand-icon">${brand.logo_text || ''}</div>
              <span class="brand-name">${brand.name}</span>
            </div>
            <p class="brand-desc">
              ${brand.description}
            </p>
          </div>

          ${footer.columns.map(column => html`
            <div>
              <div class="col-title">${column.title}</div>
              <div class="col-links">
                ${column.links.map(link => html`
                  <a class="col-link" href=${link.route ? router.pathFor(link.route) : link.href}>${link.label}</a>
                `)}
              </div>
            </div>
          `)}
        </div>

        <div class="bottom">
          <div class="copyright">${footer.copyright || ''}</div>
          <div class="social">
            ${(footer.social || []).map((social, index) => html`<a class="social-link" href=${social.href || '#'} aria-label=${social.label || ''}>
              ${index === 0 ? html`
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"/></svg>
              ` : html`
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M8 12h.01"/><path d="M16 12h.01"/><path d="M9 16c2 1 4 1 6 0"/><path d="M7.5 8.5c3-1 6-1 9 0"/><path d="M6 19c-1.5-.5-2.5-1.5-3-3 .5-4 1.5-7 3-9 1.5.5 2.5 1 3.5 2"/><path d="M18 19c1.5-.5 2.5-1.5 3-3-.5-4-1.5-7-3-9-1.5.5-2.5 1-3.5 2"/></svg>
              `}
            </a>`)}
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('site-footer', SiteFooter);
